import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import InputMask from 'react-input-mask';
import { Switch, MenuItem } from '@material-ui/core';
import { MdSearch, MdAdd, MdLocalShipping } from 'react-icons/md';
import { Dialog } from '@material-ui/core';
import TextField from 'components/TextField';
import Button from 'components/Button';
import Images from 'assets/img';
import TruckerActions from 'store/ducks/trucker';
import IssuersActions from 'store/ducks/issuers';
import Selector from 'components/Selector';
import DatePicker from 'components/DatePicker';
import {
  Container,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  GlobalSelectStyles,
  HeaderButtons,
  HeaderSaveButton,
  HeaderCancelButton,
  StepsContainer,
  Step,
  StepNumber,
  FormContent,
  LeftPanel,
  RightPanel,
  Section,
  SectionTitle,
  WrapperInput,
  Row,
  ToggleContainer,
  ToggleLabel,
  ChipsContainer,
  Chip,
  VehiclesSection,
  VehiclesHeader,
  VehiclesTitle,
  VehiclesList,
  VehicleItem,
  VehicleInfo,
  VehicleLabel,
  VehicleDetails,
  VehicleType,
  ModalFooter,
  SaveButton,
  CancelButton,
} from './styles';

const STEPS = [
  { id: 1, label: 'Dados do Motorista' },
  { id: 2, label: 'Documentos' },
  { id: 3, label: 'Veículos' },
  { id: 4, label: 'Gerenciadora de Risco' },
];

function TruckerForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const { truckers, oneTrucker } = useSelector((state) => state.trucker);
  const { issuers } = useSelector((state) => state.issuers);
  const isEdit = !!id;
  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const vehicleDocumentInputRefs = useRef({});
  const [activeStep, setActiveStep] = useState(1);
  const [searchVehicle, setSearchVehicle] = useState('');
  const [openVehicleModal, setOpenVehicleModal] = useState(false);
  const [viewingVehicleDocument, setViewingVehicleDocument] = useState(null);
  const [newVehicle, setNewVehicle] = useState({
    placa: '',
    renavam: '',
    chassi: '',
    uf_plate: '',
    cpf_cnpj_proprietario: '',
    nome_proprietario: '',
    rntc: '',
    capacidade_kg: '',
    capacidade_m3: '',
    tara_kg: '',
    tipo_rodado: '',
    tipo_carroceria: '',
    qtd_eixos: '',
    marca: '',
    modelo: '',
    ano: '',
    tipo_proprietario: 0, // 0 - TAC Agregado, 1 - TAC Independente, 2 - Outros
    tipo: 'tração', // Tipo padrão: tração ou reboque
  });

  // Usar oneTrucker quando estiver editando, caso contrário buscar na lista
  const trucker = isEdit ? (oneTrucker?.id === id ? oneTrucker : truckers.find((t) => t.id === id)) : null;

  useEffect(() => {
    if (isEdit && id) {
      // Buscar motorista específico com veículos
      dispatch(TruckerActions.getOneTruckerRequest(id));
    }
    dispatch(IssuersActions.getIssuersRequest());
  }, [dispatch, isEdit, id]);

  // Log para debug quando oneTrucker for carregado
  useEffect(() => {
    if (isEdit && oneTrucker?.id === id) {
      console.log('✅ Motorista carregado:', oneTrucker);
      console.log('✅ Veículos carregados:', oneTrucker?.veiculos);
    }
  }, [isEdit, id, oneTrucker]);

  // Função para formatar data para input type="date" (YYYY-MM-DD)
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '';
    }
  };

  // Recalcular initialValues quando trucker mudar (quando oneTrucker for carregado)
  const initialValues = useMemo(() => ({
    name: trucker?.name || '',
    cpf: trucker?.cpf || '',
    email: trucker?.email || '',
    phone: trucker?.phone || '',
    cnh: trucker?.cnh || '',
    cnh_category: trucker?.cnh_category || '',
    cnh_expiration: formatDateForInput(trucker?.cnh_expiration),
    vehicle_plate: trucker?.vehicle_plate || '',
    vehicle_model: trucker?.vehicle_model || '',
    address: trucker?.address || '',
    complement: trucker?.complement || '',
    neighborhood: trucker?.neighborhood || '',
    city: trucker?.city || '',
    state: trucker?.state || '',
    zipcode: trucker?.zipcode || '',
    vehicle_renavam: trucker?.vehicle_renavam || '',
    vehicle_owner_cpf_cnpj: trucker?.vehicle_owner_cpf_cnpj || '',
    vehicle_owner_name: trucker?.vehicle_owner_name || '',
    // Documentos do condutor
    url_cnh: trucker?.url_cnh || '',
    url_holding_cnh: trucker?.url_holding_cnh || '',
    // Documentos do veículo
    url_document: trucker?.url_document || '',
    url_holding_document: trucker?.url_holding_document || '',
    // Gerenciadora de risco
    rg: trucker?.rg || '',
    security_number: trucker?.security_number || '',
    firstlicense: formatDateForInput(trucker?.firstlicense),
    birth_date: formatDateForInput(trucker?.birth_date),
    mother_name: trucker?.mother_name || '',
    sex_trucker: trucker?.sex_trucker || '',
    date_risky: formatDateForInput(trucker?.date_risky),
    trucker_status_riscky: trucker?.trucker_status_riscky || '',
    description_analysis: trucker?.description_analysis || '',
    // Campos adicionais do veículo
    chassi: trucker?.chassi || '',
    rntc: trucker?.rntc || '',
    uf_plate: trucker?.uf_plate || '',
    capacity_kg: trucker?.capacity_kg || '',
    capacity_m3: trucker?.capacity_m3 || '',
    tara_kg: trucker?.tara_kg || '',
    type_rotated: trucker?.type_rotated || '',
    type_bodywork: trucker?.type_bodywork || '',
    qtd_axes: trucker?.qtd_axes || '',
    brand_vehicle: trucker?.brand_vehicle || '',
    model_vehicle: trucker?.model_vehicle || '',
    year_vehicle: trucker?.year_vehicle || '',
    transport_type: trucker?.transport_type || 'etc',
    issuer: trucker?.issuer || '',
    social_reason: trucker?.social_reason || '',
    limit_anticipate: trucker?.limit_anticipate || null,
    status: trucker?.status !== undefined ? trucker.status : 1,
    // Veículos do motorista (array de objetos com veiculo_id e tipo)
    veiculos: trucker?.veiculos || [],
  }), [trucker]);

  // Função para fazer upload do arquivo para o storage
  const uploadFileToStorage = async (dataUrl, fileName, folder = 'truckers/cnh') => {
    try {
      console.log('📤 Iniciando upload do arquivo:', fileName);

      // Converter data URL para blob
      const response = await fetch(dataUrl);
      if (!response.ok) {
        throw new Error('Erro ao converter data URL para blob');
      }
      const blob = await response.blob();
      console.log('✅ Blob criado, tamanho:', blob.size, 'bytes');

      // Criar FormData para enviar o arquivo
      const formData = new FormData();
      formData.append('file', blob, fileName || 'documento.pdf');
      formData.append('folder', folder);

      // Fazer upload para o backend
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

      console.log('📡 Enviando para:', `${API_BASE_URL}/api/upload`);

      const uploadResponse = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      console.log('📥 Resposta do servidor:', uploadResponse.status, uploadResponse.statusText);

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({ mensagem: 'Erro desconhecido' }));
        console.error('❌ Erro na resposta:', errorData);
        throw new Error(errorData.mensagem || errorData.erro || `Erro ${uploadResponse.status}: ${uploadResponse.statusText}`);
      }

      const uploadData = await uploadResponse.json();
      console.log('✅ Upload concluído:', uploadData);
      return uploadData.url || uploadData.path;
    } catch (error) {
      console.error('❌ Erro ao fazer upload:', error);
      throw error;
    }
  };

  // Função para fazer upload do documento do veículo
  const handleVehicleDocumentUpload = async (setFieldValue, veiculoIndex, file) => {
    try {
      if (!file) return;

      // Validar se é uma imagem ou PDF
      const isImage = file.type.startsWith('image/');
      const isPDF = file.type === 'application/pdf';

      if (!isImage && !isPDF) {
        alert('Por favor, selecione apenas arquivos de imagem ou PDF.');
        return;
      }

      // Criar URL temporária para preview
      const reader = new FileReader();
      reader.onload = async (event) => {
        const dataUrl = event.target.result;

        // Atualizar o veículo na lista com a data URL temporária
        const currentVeiculos = formRef.current?.values?.veiculos || [];
        const updatedVeiculos = [...currentVeiculos];
        updatedVeiculos[veiculoIndex] = {
          ...updatedVeiculos[veiculoIndex],
          url_document: dataUrl,
        };
        setFieldValue('veiculos', updatedVeiculos);
      };
      reader.onerror = () => {
        alert('Erro ao ler o arquivo. Por favor, tente novamente.');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Erro ao fazer upload do documento do veículo:', error);
      alert('Erro ao fazer upload do arquivo. Por favor, tente novamente.');
    }
  };

  const handleSubmit = async (values) => {
    // Adicionar empresa_id se houver empresa selecionada
    const dadosComEmpresa = { ...values };
    try {
      const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany') || 'null');
      if (selectedCompany?.id) {
        dadosComEmpresa.empresa_id = selectedCompany.id;
      }
    } catch (error) {
      console.error('Erro ao obter empresa selecionada:', error);
    }

    // Se houver url_cnh como data URL, fazer upload primeiro
    if (dadosComEmpresa.url_cnh && dadosComEmpresa.url_cnh.startsWith('data:')) {
      try {
        const fileExtension = dadosComEmpresa.url_cnh.includes('application/pdf') ? 'pdf' : 'jpg';
        const fileName = `cnh_${id || 'new'}_${Date.now()}.${fileExtension}`;
        const uploadedUrl = await uploadFileToStorage(dadosComEmpresa.url_cnh, fileName, 'truckers/cnh');
        dadosComEmpresa.url_cnh = uploadedUrl;
      } catch (error) {
        console.error('Erro ao fazer upload da CNH:', error);
        alert('Erro ao fazer upload do arquivo. Por favor, tente novamente.');
        return;
      }
    }

    // Fazer upload dos documentos dos veículos se houver e limpar IDs temporários
    if (dadosComEmpresa.veiculos && Array.isArray(dadosComEmpresa.veiculos)) {
      for (let i = 0; i < dadosComEmpresa.veiculos.length; i++) {
        const veiculo = dadosComEmpresa.veiculos[i];

        // Fazer upload do documento se houver
        if (veiculo.url_document && veiculo.url_document.startsWith('data:')) {
          try {
            const fileExtension = veiculo.url_document.includes('application/pdf') ? 'pdf' : 'jpg';
            const fileName = `veiculo_${veiculo.placa || i}_${Date.now()}.${fileExtension}`;
            const uploadedUrl = await uploadFileToStorage(veiculo.url_document, fileName, 'truckers/veiculos');
            dadosComEmpresa.veiculos[i].url_document = uploadedUrl;
          } catch (error) {
            console.error('Erro ao fazer upload do documento do veículo:', error);
            alert(`Erro ao fazer upload do documento do veículo ${veiculo.placa || i + 1}. Por favor, tente novamente.`);
            return;
          }
        }

        // Remover ID temporário se houver (veículos novos não têm veiculo_id)
        if (dadosComEmpresa.veiculos[i].id && dadosComEmpresa.veiculos[i].id.toString().startsWith('temp_')) {
          delete dadosComEmpresa.veiculos[i].id;
        }

        // Garantir que veiculo_id seja null para veículos novos (sem veiculo_id válido)
        if (!dadosComEmpresa.veiculos[i].veiculo_id || dadosComEmpresa.veiculos[i].veiculo_id.toString().startsWith('temp_')) {
          dadosComEmpresa.veiculos[i].veiculo_id = null;
        }
      }
    }

    if (isEdit) {
      dispatch(TruckerActions.updateTruckerRequest(id, dadosComEmpresa, () => history.push('/trucker')));
    } else {
      dispatch(TruckerActions.addTruckerRequest(dadosComEmpresa, () => history.push('/trucker')));
    }
  };

  // Veículos disponíveis - será preenchido pela API
  const availableVehicles = [];
  const filteredVehicles = [];

  const handleAddVehicle = (setFieldValue, vehicle, tipo) => {
    const currentVeiculos = formRef.current?.values?.veiculos || [];
    // Verificar se o veículo já foi adicionado com o mesmo tipo
    const exists = currentVeiculos.some(
      (v) => v.veiculo_id === vehicle.id && v.tipo === tipo
    );
    if (!exists) {
      setFieldValue('veiculos', [
        ...currentVeiculos,
        { veiculo_id: vehicle.id, tipo, placa: vehicle.placa, renavam: vehicle.renavam }
      ]);
    }
  };

  const handleRemoveVehicle = (setFieldValue, index) => {
    const currentVeiculos = formRef.current?.values?.veiculos || [];
    const newVeiculos = currentVeiculos.filter((_, i) => i !== index);
    setFieldValue('veiculos', newVeiculos);
  };

  const handleSaveNewVehicle = async (setFieldValue) => {
    try {
      // Validar campos obrigatórios
      if (!newVehicle.placa) {
        alert('Por favor, preencha a placa do veículo');
        return;
      }

      // Criar veículo temporário (será salvo quando salvar o motorista)
      const veiculoTemp = {
        id: `temp_${Date.now()}`,
        veiculo_id: null, // Será preenchido quando salvar no backend
        ...newVehicle,
        tipo: newVehicle.tipo || 'tração', // Usar o tipo selecionado
      };

      // Adicionar à lista de veículos
      const currentVeiculos = formRef.current?.values?.veiculos || [];
      setFieldValue('veiculos', [...currentVeiculos, veiculoTemp]);

      // Limpar formulário e fechar modal
      setNewVehicle({
        placa: '',
        renavam: '',
        chassi: '',
        uf_plate: '',
        cpf_cnpj_proprietario: '',
        nome_proprietario: '',
        rntc: '',
        capacidade_kg: '',
        capacidade_m3: '',
        tara_kg: '',
        tipo_rodado: '',
        tipo_carroceria: '',
        qtd_eixos: '',
        marca: '',
        modelo: '',
        ano: '',
        tipo_proprietario: 0,
        tipo: 'tração',
      });
      setOpenVehicleModal(false);
    } catch (error) {
      console.error('Erro ao adicionar veículo:', error);
      alert('Erro ao adicionar veículo. Por favor, tente novamente.');
    }
  };

  return (
    <GlobalSelectStyles>
      <Container>
        <ModalContainer>
        <ModalHeader>
          <ModalTitle>{isEdit ? 'EDITAR MOTORISTA' : 'NOVO MOTORISTA'}</ModalTitle>
          <HeaderButtons>
            <HeaderSaveButton
              type="button"
              onClick={() => {
                if (formRef.current) {
                  formRef.current.handleSubmit();
                }
              }}
            >
              SALVAR
            </HeaderSaveButton>
            <HeaderCancelButton
              type="button"
              onClick={() => history.push('/trucker')}
            >
              VOLTAR
            </HeaderCancelButton>
          </HeaderButtons>
        </ModalHeader>

        <StepsContainer>
          {STEPS.map((step) => (
            <Step
              key={step.id}
              active={activeStep === step.id}
              onClick={() => setActiveStep(step.id)}
            >
              <StepNumber active={activeStep === step.id}>
                {step.id}.
              </StepNumber>
              {step.label}
            </Step>
          ))}
        </StepsContainer>

        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, handleChange, setFieldValue, handleSubmit: formikHandleSubmit, errors, touched }) => (
            <form onSubmit={formikHandleSubmit}>
              {activeStep === 1 && (
                <FormContent>
                  <LeftPanel>
                    <Section>
                      <SectionTitle>Dados Pessoais</SectionTitle>
                      <Row>
                        <WrapperInput>
                          <TextField
                            label="Nome Completo *"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>

                        <WrapperInput>
                          <TextField
                            label="CPF *"
                            name="cpf"
                            value={values.cpf}
                            onChange={handleChange}
                            error={touched.cpf && Boolean(errors.cpf)}
                            helperText={touched.cpf && errors.cpf}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                      </Row>
                      <Row>
                        <WrapperInput>
                          <TextField
                            label="Email"
                            name="email"
                            type="email"
                            value={values.email}
                            onChange={handleChange}
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>

                        <WrapperInput>
                          <InputMask
                            mask="(99) 99999-9999"
                            value={values.phone || ''}
                            onChange={handleChange}
                            maskChar={null}
                          >
                            {(inputProps) => (
                              <TextField
                                {...inputProps}
                                label="Telefone *"
                                name="phone"
                                error={touched.phone && Boolean(errors.phone)}
                                helperText={touched.phone && errors.phone}
                                fullWidth
                                color="secondary"
                              />
                            )}
                          </InputMask>
                        </WrapperInput>
                      </Row>
                    </Section>

                    <Section>
                      <SectionTitle>Endereço</SectionTitle>
                      <Row>
                        <WrapperInput>
                          <TextField
                            label="Endereço"
                            name="address"
                            value={values.address || ''}
                            onChange={handleChange}
                            error={touched.address && Boolean(errors.address)}
                            helperText={touched.address && errors.address}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>

                        <WrapperInput>
                          <TextField
                            label="Complemento"
                            name="complement"
                            value={values.complement}
                            onChange={handleChange}
                            error={touched.complement && Boolean(errors.complement)}
                            helperText={touched.complement && errors.complement}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>

                      </Row>
                      <Row>
                        <WrapperInput>
                          <TextField
                            label="Bairro"
                            name="neighborhood"
                            value={values.neighborhood}
                            onChange={handleChange}
                            error={touched.neighborhood && Boolean(errors.neighborhood)}
                            helperText={touched.neighborhood && errors.neighborhood}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>

                        <WrapperInput>
                          <TextField
                            label="Cidade"
                            name="city"
                            value={values.city}
                            onChange={handleChange}
                            error={touched.city && Boolean(errors.city)}
                            helperText={touched.city && errors.city}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                      </Row>
                      <Row>
                        <WrapperInput>
                          <TextField
                            label="Estado (UF)"
                            name="state"
                            value={values.state}
                            onChange={handleChange}
                            error={touched.state && Boolean(errors.state)}
                            helperText={touched.state && errors.state}
                            select
                            fullWidth
                            color="secondary"
                          >
                            {[
                              { text: 'Selecione', value: '' },
                              { text: 'AC - Acre', value: 'AC' },
                              { text: 'AL - Alagoas', value: 'AL' },
                              { text: 'AP - Amapá', value: 'AP' },
                              { text: 'AM - Amazonas', value: 'AM' },
                              { text: 'BA - Bahia', value: 'BA' },
                              { text: 'CE - Ceará', value: 'CE' },
                              { text: 'DF - Distrito Federal', value: 'DF' },
                              { text: 'ES - Espírito Santo', value: 'ES' },
                              { text: 'GO - Goiás', value: 'GO' },
                              { text: 'MA - Maranhão', value: 'MA' },
                              { text: 'MT - Mato Grosso', value: 'MT' },
                              { text: 'MS - Mato Grosso do Sul', value: 'MS' },
                              { text: 'MG - Minas Gerais', value: 'MG' },
                              { text: 'PA - Pará', value: 'PA' },
                              { text: 'PB - Paraíba', value: 'PB' },
                              { text: 'PR - Paraná', value: 'PR' },
                              { text: 'PE - Pernambuco', value: 'PE' },
                              { text: 'PI - Piauí', value: 'PI' },
                              { text: 'RJ - Rio de Janeiro', value: 'RJ' },
                              { text: 'RN - Rio Grande do Norte', value: 'RN' },
                              { text: 'RS - Rio Grande do Sul', value: 'RS' },
                              { text: 'RO - Rondônia', value: 'RO' },
                              { text: 'RR - Roraima', value: 'RR' },
                              { text: 'SC - Santa Catarina', value: 'SC' },
                              { text: 'SP - São Paulo', value: 'SP' },
                              { text: 'SE - Sergipe', value: 'SE' },
                              { text: 'TO - Tocantins', value: 'TO' },
                            ].map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.text}
                              </MenuItem>
                            ))}
                          </TextField>
                        </WrapperInput>

                        <WrapperInput>
                          <TextField
                            label="CEP"
                            name="zipcode"
                            value={values.zipcode}
                            onChange={handleChange}
                            error={touched.zipcode && Boolean(errors.zipcode)}
                            helperText={touched.zipcode && errors.zipcode}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                      </Row>
                    </Section>
                  </LeftPanel>
                  <RightPanel>
                    {/* Campos adicionais podem ser adicionados aqui */}
                  </RightPanel>
                </FormContent>
              )}

              {activeStep === 2 && (
                <FormContent>
                  <LeftPanel>
                    <Section>
                      <SectionTitle>Dados da CNH</SectionTitle>
                      <Row>
                        <WrapperInput>
                          <TextField
                            label="CNH"
                            name="cnh"
                            value={values.cnh}
                            onChange={handleChange}
                            error={touched.cnh && Boolean(errors.cnh)}
                            helperText={touched.cnh && errors.cnh}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>

                        <WrapperInput>
                          <TextField
                            label="Categoria CNH"
                            name="cnh_category"
                            value={values.cnh_category}
                            onChange={handleChange}
                            error={touched.cnh_category && Boolean(errors.cnh_category)}
                            helperText={touched.cnh_category && errors.cnh_category}
                            select
                            fullWidth
                            color="secondary"
                          >
                            {[
                              { text: 'Selecione', value: '' },
                              { text: 'A - Motocicleta', value: 'A' },
                              { text: 'B - Automóvel', value: 'B' },
                              { text: 'C - Caminhão', value: 'C' },
                              { text: 'D - Ônibus', value: 'D' },
                              { text: 'E - Carreta', value: 'E' },
                              { text: 'AB - Motocicleta e Automóvel', value: 'AB' },
                              { text: 'AC - Motocicleta e Caminhão', value: 'AC' },
                              { text: 'AD - Motocicleta e Ônibus', value: 'AD' },
                              { text: 'AE - Motocicleta e Carreta', value: 'AE' },
                            ].map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.text}
                              </MenuItem>
                            ))}
                          </TextField>
                        </WrapperInput>
                      </Row>
                      <Row>
                        <WrapperInput>
                          <TextField
                            label="Validade CNH"
                            name="cnh_expiration"
                            type="date"
                            value={values.cnh_expiration || ''}
                            onChange={(e) => {
                              const dateValue = e.target.value || '';
                              handleChange({
                                ...e,
                                target: {
                                  ...e.target,
                                  name: 'cnh_expiration',
                                  value: dateValue,
                                },
                              });
                            }}
                            error={touched.cnh_expiration && Boolean(errors.cnh_expiration)}
                            helperText={touched.cnh_expiration && errors.cnh_expiration}
                            fullWidth
                            color="secondary"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </WrapperInput>

                        <WrapperInput>
                          <TextField
                            label="Limite Antecipação"
                            name="limit_anticipate"
                            type="number"
                            value={values.limit_anticipate || ''}
                            onChange={handleChange}
                            error={touched.limit_anticipate && Boolean(errors.limit_anticipate)}
                            helperText={touched.limit_anticipate && errors.limit_anticipate}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                      </Row>
                    </Section>
                    <Section>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <SectionTitle>Documentos do Condutor</SectionTitle>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*,application/pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0];
                            if (file) {
                              // Validar se é uma imagem ou PDF
                              const isImage = file.type.startsWith('image/');
                              const isPDF = file.type === 'application/pdf';

                              if (!isImage && !isPDF) {
                                alert('Por favor, selecione apenas arquivos de imagem ou PDF.');
                                return;
                              }

                              // Criar URL temporária para preview
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                const result = event.target.result;
                                setFieldValue('url_cnh', result);
                              };
                              reader.onerror = () => {
                                alert('Erro ao ler o arquivo. Por favor, tente novamente.');
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                        <Button
                          iconSize={20}
                          label="Foto da CNH Motorista"
                          onClick={() => {
                            if (fileInputRef.current) {
                              fileInputRef.current.click();
                            }
                          }}
                          type="secondary"
                          width="200px"
                          height="40px"
                        />
                      </div>
                      {values.url_cnh && (
                        <WrapperInput>
                          <div style={{ marginTop: '20px' }}>
                            {values.url_cnh.startsWith('data:image/') || (values.url_cnh.startsWith('http') && !values.url_cnh.endsWith('.pdf')) ? (
                              <div>
                                <img
                                  src={values.url_cnh}
                                  alt="CNH Motorista"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '300px',
                                    borderRadius: '4px',
                                    border: '1px solid #e0e0e0',
                                    display: 'block',
                                    marginBottom: '10px'
                                  }}
                                />
                                <Button
                                  label="Remover Arquivo"
                                  onClick={() => {
                                    setFieldValue('url_cnh', '');
                                    if (fileInputRef.current) {
                                      fileInputRef.current.value = '';
                                    }
                                  }}
                                  type="button"
                                  width="150px"
                                  height="35px"
                                />
                              </div>
                            ) : values.url_cnh.startsWith('data:application/pdf') || values.url_cnh.endsWith('.pdf') || (values.url_cnh.startsWith('http') && values.url_cnh.endsWith('.pdf')) ? (
                              <div>
                                <div style={{
                                  padding: '20px',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '4px',
                                  backgroundColor: '#f5f5f5',
                                  textAlign: 'center',
                                  marginBottom: '10px'
                                }}>
                                  <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                                    Arquivo PDF selecionado
                                  </div>
                                  {values.url_cnh.startsWith('http') ? (
                                    <a
                                      href={values.url_cnh}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: '#122D58',
                                        textDecoration: 'underline',
                                        fontSize: '14px'
                                      }}
                                    >
                                      Abrir PDF
                                    </a>
                                  ) : (
                                    <div style={{ fontSize: '12px', color: '#999' }}>
                                      O arquivo será salvo ao confirmar
                                    </div>
                                  )}
                                </div>
                                <Button
                                  label="Remover Arquivo"
                                  onClick={() => {
                                    setFieldValue('url_cnh', '');
                                    if (fileInputRef.current) {
                                      fileInputRef.current.value = '';
                                    }
                                  }}
                                  type="button"
                                  width="150px"
                                  height="35px"
                                />
                              </div>
                            ) : (
                              <div>
                                <TextField
                                  label="Arquivo selecionado"
                                  value={values.url_cnh}
                                  disabled
                                  fullWidth
                                  color="secondary"
                                />
                                <Button
                                  label="Remover Arquivo"
                                  onClick={() => {
                                    setFieldValue('url_cnh', '');
                                    if (fileInputRef.current) {
                                      fileInputRef.current.value = '';
                                    }
                                  }}
                                  type="button"
                                  width="150px"
                                  height="35px"
                                  style={{ marginTop: '10px' }}
                                />
                              </div>
                            )}
                          </div>
                        </WrapperInput>
                      )}
                    </Section>
                  </LeftPanel>
                </FormContent>
              )}

              {activeStep === 3 && (
                <VehiclesSection>
                  <VehiclesHeader>
                    <VehiclesTitle>Veículos do Motorista</VehiclesTitle>
                  </VehiclesHeader>
                  <FormContent>
                    <LeftPanel>
                      <Section>
                        <SectionTitle>Buscar Veículo</SectionTitle>
                        <WrapperInput>
                          <TextField
                            label="Buscar por placa ou RENAVAM"
                            value={searchVehicle}
                            onChange={(e) => setSearchVehicle(e.target.value)}
                            fullWidth
                            color="secondary"
                            InputProps={{
                              endAdornment: (
                                <MdSearch style={{ cursor: 'pointer', color: '#122D58', marginRight: '8px' }} />
                              ),
                            }}
                          />
                        </WrapperInput>
                        <ChipsContainer>
                          {filteredVehicles && filteredVehicles.length > 0 ? (
                            filteredVehicles.map((vehicle) => (
                              <div key={vehicle.id} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <Chip
                                  onClick={() => handleAddVehicle(setFieldValue, vehicle, 'tração')}
                                >
                                  {vehicle.placa} - Tração
                                </Chip>
                                <Chip
                                  onClick={() => handleAddVehicle(setFieldValue, vehicle, 'reboque')}
                                >
                                  {vehicle.placa} - Reboque
                                </Chip>
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                              {searchVehicle ? 'Nenhum veículo encontrado' : 'Digite a placa ou RENAVAM para buscar'}
                            </div>
                          )}
                        </ChipsContainer>
                      </Section>
                    </LeftPanel>
                    <RightPanel>
                      <Section>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <SectionTitle>Veículos Adicionados</SectionTitle>
                          <Button
                            iconSize={20}
                            label="+ Adicionar Veículo"
                            onClick={() => setOpenVehicleModal(true)}
                            type="secondary"
                            width="180px"
                            height="40px"
                          />
                        </div>
                        <VehiclesList>
                          {values.veiculos && values.veiculos.length > 0 ? (
                            values.veiculos.map((veiculo, index) => (
                              <VehicleItem key={index} tipo={veiculo.tipo}>
                                <VehicleInfo>
                                  <VehicleLabel>{veiculo.placa || 'N/A'}</VehicleLabel>
                                  <VehicleDetails>
                                    RENAVAM: {veiculo.renavam || 'N/A'}
                                  </VehicleDetails>
                                </VehicleInfo>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                  <VehicleType tipo={veiculo.tipo}>
                                    {veiculo.tipo}
                                  </VehicleType>
                                  <input
                                    ref={(el) => {
                                      if (el) {
                                        vehicleDocumentInputRefs.current[`vehicle_${index}`] = el;
                                      }
                                    }}
                                    type="file"
                                    accept="image/*,application/pdf"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                      const file = e.target.files && e.target.files[0];
                                      if (file) {
                                        handleVehicleDocumentUpload(setFieldValue, index, file);
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const inputRef = vehicleDocumentInputRefs.current[`vehicle_${index}`];
                                      if (inputRef) {
                                        inputRef.click();
                                      }
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      background: '#FFF28D',
                                      border: 'none',
                                      borderRadius: '4px',
                                      color: '#122D58',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                    }}
                                  >
                                    {veiculo.url_document ? 'Alterar Doc.' : 'Adicionar Doc.'}
                                  </button>
                                  {veiculo.url_document && (
                                    <>
                                      <span style={{ fontSize: '12px', color: '#4CAF50' }}>✓</span>
                                      <button
                                        type="button"
                                        onClick={() => setViewingVehicleDocument({ index, url: veiculo.url_document, placa: veiculo.placa })}
                                        style={{
                                          padding: '6px 12px',
                                          background: '#4CAF50',
                                          border: 'none',
                                          borderRadius: '4px',
                                          color: '#FFFFFF',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                        }}
                                      >
                                        Visualizar
                                      </button>
                                    </>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveVehicle(setFieldValue, index)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#F44336',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: '600',
                                    }}
                                  >
                                    Remover
                                  </button>
                                </div>
                              </VehicleItem>
                            ))
                          ) : (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                              Nenhum veículo adicionado
                            </div>
                          )}
                        </VehiclesList>
                      </Section>
                    </RightPanel>
                  </FormContent>
                </VehiclesSection>
              )}

              {/* Modal de Cadastro de Veículo */}
              <Dialog
                open={openVehicleModal}
                onClose={() => setOpenVehicleModal(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                  style: {
                    borderRadius: 0,
                    maxHeight: '90vh',
                    overflow: 'hidden',
                  },
                }}
              >
                <ModalHeader>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <MdLocalShipping size={24} />
                    <ModalTitle>CADASTRAR NOVO VEÍCULO</ModalTitle>
                  </div>
                  <HeaderButtons>
                    <HeaderSaveButton
                      type="button"
                      onClick={() => {
                        if (formRef.current) {
                          handleSaveNewVehicle(formRef.current.setFieldValue);
                        }
                      }}
                    >
                      SALVAR
                    </HeaderSaveButton>
                    <HeaderCancelButton
                      type="button"
                      onClick={() => setOpenVehicleModal(false)}
                    >
                      CANCELAR
                    </HeaderCancelButton>
                  </HeaderButtons>
                </ModalHeader>
                <div style={{ padding: '24px', maxHeight: 'calc(90vh - 80px)', overflowY: 'auto' }}>
                  <FormContent>
                    <LeftPanel>
                      <Section>
                        <SectionTitle>Dados do Veículo</SectionTitle>
                        <Row>
                          <WrapperInput>
                            <TextField
                              label="Placa *"
                              value={newVehicle.placa}
                              onChange={(e) => setNewVehicle({ ...newVehicle, placa: e.target.value.toUpperCase() })}
                              fullWidth
                              color="secondary"
                            />
                          </WrapperInput>
                          <WrapperInput>
                            <TextField
                              label="RENAVAM"
                              value={newVehicle.renavam}
                              onChange={(e) => setNewVehicle({ ...newVehicle, renavam: e.target.value })}
                              fullWidth
                              color="secondary"
                            />
                          </WrapperInput>
                        </Row>
                        <Row>
                          <WrapperInput>
                            <TextField
                              label="Chassi"
                              value={newVehicle.chassi}
                              onChange={(e) => setNewVehicle({ ...newVehicle, chassi: e.target.value })}
                              fullWidth
                              color="secondary"
                            />
                          </WrapperInput>
                          <WrapperInput>
                            <TextField
                              label="UF da Placa"
                              value={newVehicle.uf_plate}
                              onChange={(e) => setNewVehicle({ ...newVehicle, uf_plate: e.target.value.toUpperCase() })}
                              fullWidth
                              color="secondary"
                              inputProps={{ maxLength: 2 }}
                            />
                          </WrapperInput>
                        </Row>
                        <Row>
                          <WrapperInput>
                            <TextField
                              label="Marca"
                              value={newVehicle.marca}
                              onChange={(e) => setNewVehicle({ ...newVehicle, marca: e.target.value })}
                              fullWidth
                              color="secondary"
                            />
                          </WrapperInput>
                          <WrapperInput>
                            <TextField
                              label="Modelo"
                              value={newVehicle.modelo}
                              onChange={(e) => setNewVehicle({ ...newVehicle, modelo: e.target.value })}
                              fullWidth
                              color="secondary"
                            />
                          </WrapperInput>
                        </Row>
                        <Row>
                          <WrapperInput>
                            <TextField
                              label="Ano"
                              type="number"
                              value={newVehicle.ano}
                              onChange={(e) => setNewVehicle({ ...newVehicle, ano: e.target.value })}
                              fullWidth
                              color="secondary"
                            />
                          </WrapperInput>
                          <WrapperInput>
                            <TextField
                              label="Tipo de Rodado"
                              value={newVehicle.tipo_rodado}
                              onChange={(e) => setNewVehicle({ ...newVehicle, tipo_rodado: e.target.value })}
                              select
                              fullWidth
                              color="secondary"
                            >
                              {[
                                { text: 'Selecione', value: '' },
                                { text: 'Truck', value: 'Truck' },
                                { text: 'Toco', value: 'Toco' },
                                { text: 'Cavalo Mecânico', value: 'Cavalo Mecânico' },
                                { text: 'VAN', value: 'VAN' },
                                { text: 'Utilitário', value: 'Utilitário' },
                              ].map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.text}
                                </MenuItem>
                              ))}
                            </TextField>
                          </WrapperInput>
                        </Row>
                        <Row>
                          <WrapperInput>
                            <TextField
                              label="Tipo de Carroceria"
                              value={newVehicle.tipo_carroceria}
                              onChange={(e) => setNewVehicle({ ...newVehicle, tipo_carroceria: e.target.value })}
                              select
                              fullWidth
                              color="secondary"
                            >
                              {[
                                { text: 'Selecione', value: '' },
                                { text: 'Aberta', value: 'Aberta' },
                                { text: 'Fechada/Baú', value: 'Fechada/Baú' },
                                { text: 'Graneleira', value: 'Graneleira' },
                                { text: 'Porta Container', value: 'Porta Container' },
                                { text: 'Sider', value: 'Sider' },
                              ].map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.text}
                                </MenuItem>
                              ))}
                            </TextField>
                          </WrapperInput>
                          <WrapperInput>
                            <TextField
                              label="Quantidade de Eixos"
                              type="number"
                              value={newVehicle.qtd_eixos}
                              onChange={(e) => setNewVehicle({ ...newVehicle, qtd_eixos: e.target.value })}
                              fullWidth
                              color="secondary"
                            />
                          </WrapperInput>
                        </Row>
                        <Row>
                          <WrapperInput>
                            <TextField
                              label="Tipo *"
                              value={newVehicle.tipo || 'tração'}
                              onChange={(e) => setNewVehicle({ ...newVehicle, tipo: e.target.value })}
                              select
                              fullWidth
                              color="secondary"
                            >
                              {[
                                { text: 'Tração', value: 'tração' },
                                { text: 'Reboque', value: 'reboque' },
                              ].map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.text}
                                </MenuItem>
                              ))}
                            </TextField>
                          </WrapperInput>
                        </Row>
                      </Section>
                    </LeftPanel>
                    <RightPanel>
                      <Section>
                        <SectionTitle>Capacidade e Dimensões</SectionTitle>
                        <Row>
                          <WrapperInput>
                            <TextField
                              label="Capacidade (kg)"
                              type="number"
                              value={newVehicle.capacidade_kg}
                              onChange={(e) => setNewVehicle({ ...newVehicle, capacidade_kg: e.target.value })}
                              fullWidth
                              color="secondary"
                            />
                          </WrapperInput>
                          <WrapperInput>
                            <TextField
                              label="Capacidade (m³)"
                              type="number"
                              value={newVehicle.capacidade_m3}
                              onChange={(e) => setNewVehicle({ ...newVehicle, capacidade_m3: e.target.value })}
                              fullWidth
                              color="secondary"
                            />
                          </WrapperInput>
                        </Row>
                        <WrapperInput>
                          <TextField
                            label="Tara (kg)"
                            type="number"
                            value={newVehicle.tara_kg}
                            onChange={(e) => setNewVehicle({ ...newVehicle, tara_kg: e.target.value })}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                      </Section>
                      <Section>
                        <SectionTitle>Proprietário</SectionTitle>
                        <WrapperInput>
                          <TextField
                            label="CPF/CNPJ do Proprietário"
                            value={newVehicle.cpf_cnpj_proprietario}
                            onChange={(e) => setNewVehicle({ ...newVehicle, cpf_cnpj_proprietario: e.target.value })}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                        <WrapperInput>
                          <TextField
                            label="Nome do Proprietário"
                            value={newVehicle.nome_proprietario}
                            onChange={(e) => setNewVehicle({ ...newVehicle, nome_proprietario: e.target.value })}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                        <WrapperInput>
                          <TextField
                            label="RNTC"
                            value={newVehicle.rntc}
                            onChange={(e) => setNewVehicle({ ...newVehicle, rntc: e.target.value })}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                        <WrapperInput>
                          <TextField
                            label="Tipo Proprietário *"
                            value={newVehicle.tipo_proprietario}
                            onChange={(e) => setNewVehicle({ ...newVehicle, tipo_proprietario: parseInt(e.target.value) || 0 })}
                            select
                            fullWidth
                            color="secondary"
                          >
                            {[
                              { text: 'TAC Agregado', value: 0 },
                              { text: 'TAC Independente', value: 1 },
                              { text: 'Outros', value: 2 },
                            ].map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.text}
                              </MenuItem>
                            ))}
                          </TextField>
                        </WrapperInput>
                      </Section>
                    </RightPanel>
                  </FormContent>
                </div>
              </Dialog>

              {activeStep === 4 && (
                <FormContent>
                  <LeftPanel>
                    <Section>
                      <SectionTitle>Gerenciadora de Risco</SectionTitle>
                      <Row>
                        <WrapperInput>
                          <TextField
                            label="Data 1º Habilitação"
                            name="firstlicense"
                            type="date"
                            value={values.firstlicense || ''}
                            onChange={(e) => {
                              const dateValue = e.target.value || '';
                              handleChange({
                                ...e,
                                target: {
                                  ...e.target,
                                  name: 'firstlicense',
                                  value: dateValue,
                                },
                              });
                            }}
                            error={touched.firstlicense && Boolean(errors.firstlicense)}
                            helperText={touched.firstlicense && errors.firstlicense}
                            fullWidth
                            color="secondary"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </WrapperInput>

                        <WrapperInput>
                          <TextField
                            label="Código Segurança da CNH"
                            name="security_number"
                            type="number"
                            value={values.security_number || ''}
                            onChange={handleChange}
                            error={touched.security_number && Boolean(errors.security_number)}
                            helperText={touched.security_number && errors.security_number}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                      </Row>
                      <Row>
                        <WrapperInput>
                          <TextField
                            label="Nome da Mãe"
                            name="mother_name"
                            value={values.mother_name}
                            onChange={handleChange}
                            error={touched.mother_name && Boolean(errors.mother_name)}
                            helperText={touched.mother_name && errors.mother_name}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>

                        <WrapperInput>
                          <TextField
                            label="RG"
                            name="rg"
                            type="number"
                            value={values.rg || ''}
                            onChange={handleChange}
                            error={touched.rg && Boolean(errors.rg)}
                            helperText={touched.rg && errors.rg}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                      </Row>
                      <Row>
                        <WrapperInput>
                          <TextField
                            label="Data de Nascimento"
                            name="birth_date"
                            type="date"
                            value={values.birth_date || ''}
                            onChange={(e) => {
                              const dateValue = e.target.value || '';
                              handleChange({
                                ...e,
                                target: {
                                  ...e.target,
                                  name: 'birth_date',
                                  value: dateValue,
                                },
                              });
                            }}
                            error={touched.birth_date && Boolean(errors.birth_date)}
                            helperText={touched.birth_date && errors.birth_date}
                            fullWidth
                            color="secondary"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </WrapperInput>

                        <WrapperInput>
                          <TextField
                            label="Sexo"
                            name="sex_trucker"
                            value={values.sex_trucker}
                            onChange={handleChange}
                            select
                            fullWidth
                            color="secondary"
                          >
                            {[
                              { text: 'Selecione', value: '' },
                              { text: 'Masculino', value: 'masculine' },
                              { text: 'Feminino', value: 'feminine' },
                            ].map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.text}
                              </MenuItem>
                            ))}
                          </TextField>
                        </WrapperInput>
                      </Row>
                    </Section>
                  </LeftPanel>
                  <RightPanel>
                    <Section>
                      <SectionTitle>Análise de Risco</SectionTitle>
                      <Row>
                        <WrapperInput>
                          <TextField
                            label="Data da Consulta"
                            name="date_risky"
                            type="date"
                            value={values.date_risky || ''}
                            onChange={handleChange}
                            fullWidth
                            color="secondary"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </WrapperInput>

                        <WrapperInput>
                          <TextField
                            label="Risco Condutor"
                            name="trucker_status_riscky"
                            value={values.trucker_status_riscky}
                            onChange={handleChange}
                            select
                            fullWidth
                            color="secondary"
                          >
                            {[
                              { text: 'Sem avaliação', value: '' },
                              { text: 'Aprovado', value: 'aproved' },
                              { text: 'Reprovado', value: 'reproved' },
                            ].map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.text}
                              </MenuItem>
                            ))}
                          </TextField>
                        </WrapperInput>

                      </Row>
                      <WrapperInput>
                        <TextField
                          label="Retorno Análise"
                          name="description_analysis"
                          value={values.description_analysis}
                          onChange={handleChange}
                          error={touched.description_analysis && Boolean(errors.description_analysis)}
                          helperText={touched.description_analysis && errors.description_analysis}
                          fullWidth
                          color="secondary"
                          multiline
                          rows={4}
                        />
                      </WrapperInput>
                    </Section>
                  </RightPanel>
                </FormContent>
              )}
            </form>
          )}
        </Formik>
      </ModalContainer>

      {/* Modal para visualizar documento do veículo */}
      <Dialog
        open={!!viewingVehicleDocument}
        onClose={() => setViewingVehicleDocument(null)}
        maxWidth="md"
        fullWidth
      >
        <div style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#122D58' }}>
              Documento do Veículo - {viewingVehicleDocument?.placa || 'N/A'}
            </h2>
            <button
              onClick={() => setViewingVehicleDocument(null)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ×
            </button>
          </div>

          {viewingVehicleDocument?.url && (
            <div style={{ textAlign: 'center' }}>
              {viewingVehicleDocument.url.startsWith('data:image/') ||
                (viewingVehicleDocument.url.startsWith('http') && !viewingVehicleDocument.url.endsWith('.pdf')) ? (
                <img
                  src={viewingVehicleDocument.url}
                  alt="Documento do Veículo"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    borderRadius: '4px',
                    border: '1px solid #e0e0e0',
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
              ) : viewingVehicleDocument.url.startsWith('data:application/pdf') ||
                viewingVehicleDocument.url.endsWith('.pdf') ||
                (viewingVehicleDocument.url.startsWith('http') && viewingVehicleDocument.url.endsWith('.pdf')) ? (
                <div>
                  <div style={{
                    padding: '40px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    backgroundColor: '#f5f5f5',
                    textAlign: 'center',
                    marginBottom: '20px'
                  }}>
                    <div style={{ fontSize: '64px', marginBottom: '20px' }}>📄</div>
                    <div style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>
                      Arquivo PDF
                    </div>
                    {viewingVehicleDocument.url.startsWith('http') ? (
                      <a
                        href={viewingVehicleDocument.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-block',
                          padding: '10px 20px',
                          backgroundColor: '#122D58',
                          color: '#FFFFFF',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontWeight: '600',
                        }}
                      >
                        Abrir PDF em Nova Aba
                      </a>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#999' }}>
                        O PDF será salvo quando você confirmar o cadastro
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{
                  padding: '40px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: '#f5f5f5',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>📎</div>
                  <div style={{ fontSize: '16px', color: '#666' }}>
                    Arquivo anexado
                  </div>
                  {viewingVehicleDocument.url.startsWith('http') && (
                    <a
                      href={viewingVehicleDocument.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        marginTop: '20px',
                        padding: '10px 20px',
                        backgroundColor: '#122D58',
                        color: '#FFFFFF',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontWeight: '600',
                      }}
                    >
                      Abrir Arquivo
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        </Dialog>
      </Container>
    </GlobalSelectStyles>
  );
}

export default TruckerForm;

